/**
 * Custom Carousel
 *
 * Vollständig Bootstrap-unabhängige Implementierung.
 * Layout: Flex-Track – alle Items liegen nebeneinander,
 * der Track wird per transform: translateX() verschoben.
 *
 * Features:
 * - Slide-Animation (Track-Translation) oder Fade (data-transition="fade")
 * - Dot-Navigation (data-dots – optionale Ergänzung, Prev/Next immer sichtbar)
 * - Autoplay mit konfigurierbarem Intervall und Bounce-Richtungsumkehr
 * - Pause-on-Hover (immer aktiv)
 * - Pause-Button (WCAG 2.2.2)
 * - Mouse/Touch-Drag (data-drag) mit Randwiderstand, kein Loop
 * - prefers-reduced-motion Support
 * - Keyboard-Navigation (ArrowLeft/ArrowRight)
 * - aria-live-Ankündigungen nur bei manueller Navigation (nicht beim Autoplay)
 */

import { createPauseButton, type PauseButtonInstance } from './utils/pause-button';

type Transition = 'slide' | 'fade';

interface CarouselConfig {
    autoplay: boolean;
    interval: number;
    transition: Transition;
    dots: boolean;
    drag: boolean;
    labelPrev: string;
    labelNext: string;
}

class Carousel {
    private readonly el: HTMLElement;
    private readonly track: HTMLElement;
    private readonly items: HTMLElement[];
    private prevBtn: HTMLElement | null = null;
    private nextBtn: HTMLElement | null = null;
    private readonly config: CarouselConfig;
    private activeIndex: number;
    private direction = 1; // Autoplay-Richtung: +1 vorwärts, -1 rückwärts
    private timer: ReturnType<typeof setInterval> | null = null;
    private isPlaying = false;
    private isAnimating = false;
    private isUserInteraction = false;
    private isDragged = false;
    private announcer: HTMLElement | null = null;
    private pauseButton: PauseButtonInstance | null = null;
    private dots: HTMLButtonElement[] = [];

    constructor(el: HTMLElement) {
        this.el = el;
        this.track = el.querySelector<HTMLElement>('.carousel__track')!;
        this.items = Array.from(el.querySelectorAll<HTMLElement>('.carousel__slide'));
        this.activeIndex = this.items.findIndex(item => item.hasAttribute('data-active'));
        if (this.activeIndex === -1) this.activeIndex = 0;

        this.config = this.parseConfig();
        this.bindSlideLinks();
        this.initSlideImages();

        if (this.items.length < 2) return;

        this.createNavButtons();
        this.initTrackPosition();
        this.updateAriaHidden();
        this.handleReducedMotion();
        this.bindEvents();
        this.initNav();
        this.initAnnouncer();

        if (this.config.drag) {
            this.bindDragEvents();
        }

        if (this.config.autoplay) {
            this.addPauseButton();
            // Autoplay erst starten, wenn das Bild im aktiven Slide geladen ist –
            // damit der Timer nicht schon tickt während der Slide noch leer ist.
            const activeImg = this.items[this.activeIndex]?.querySelector<HTMLImageElement>('img');
            if (!activeImg || activeImg.complete) {
                this.play();
            } else {
                const startOnLoad = () => this.play();
                activeImg.addEventListener('load', startOnLoad, { once: true });
                activeImg.addEventListener('error', startOnLoad, { once: true });
            }
        }
    }

    private parseConfig(): CarouselConfig {
        const interval = this.el.dataset.interval;
        return {
            autoplay: this.el.hasAttribute('data-autoplay'),
            interval: interval === 'false' ? 0 : parseInt(interval || '5000', 10),
            transition: this.el.dataset.transition === 'fade' ? 'fade' : 'slide',
            dots: this.el.hasAttribute('data-dots'),
            drag: this.el.hasAttribute('data-drag'),
            labelPrev: this.el.dataset.labelPrev ?? 'Vorheriger Slide',
            labelNext: this.el.dataset.labelNext ?? 'Nächster Slide',
        };
    }

    /**
     * Für jeden Slide: wartet auf das Laden des enthaltenen <img> und setzt
     * dann das data-Attribut `data-loaded`. Slides ohne Bild erhalten sie sofort.
     * Ermöglicht den CSS-gesteuerten Fade-in via @media (scripting: enabled).
     */
    private initSlideImages(): void {
        this.items.forEach((slide) => {
            const img = slide.querySelector<HTMLImageElement>('img');
            const markLoaded = () => slide.setAttribute('data-loaded', '');
            if (!img) {
                markLoaded();
                return;
            }
            if (img.complete) {
                markLoaded();
            } else {
                img.addEventListener('load', markLoaded, { once: true });
                img.addEventListener('error', markLoaded, { once: true });
            }
        });
    }

    /** Setzt die initiale Track-Position ohne Animation */
    private initTrackPosition(): void {
        if (this.config.transition === 'fade') return;
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(-${(this.activeIndex / this.items.length) * 100}%)`;
        void this.track.offsetHeight;
        this.track.style.transition = '';
    }

    private handleReducedMotion(): void {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) {
            this.config.autoplay = false;
            this.el.style.setProperty('--carousel-duration', '0s');
        }
    }

    private bindEvents(): void {
        this.prevBtn?.addEventListener('click', () => this.prev());
        this.nextBtn?.addEventListener('click', () => this.next());

        // Hover pausiert Timer temporär – resumt bei mouseleave (falls isPlaying)
        this.el.addEventListener('mouseenter', () => this.stopTimer());
        this.el.addEventListener('mouseleave', () => {
            if (this.isPlaying) this.startTimer();
        });

        // Fokus pausiert Timer temporär – kein Zustandswechsel, kein Button-Label-Wechsel
        this.el.addEventListener('focusin', () => this.stopTimer());
        this.el.addEventListener('focusout', (e: FocusEvent) => {
            if (!this.isPlaying) return;
            if (this.el.contains(e.relatedTarget as Node)) return;
            this.startTimer();
        });

        this.el.addEventListener('keydown', (e) => this.handleKeydown(e));
    }

    private handleKeydown(e: KeyboardEvent): void {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.prev();
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.next();
        }
    }

    // ─── Navigation ──────────────────────────────────────────────────────────

    /** Erstellt Prev/Next-Buttons aus den <template data-carousel-icon>-Elementen im HTML. */
    private createNavButtons(): void {
        const makekBtn = (icon: 'prev' | 'next', label: string): HTMLButtonElement => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = icon === 'prev' ? 'carousel__prev' : 'carousel__next';
            btn.setAttribute('aria-label', label);
            const svgIconTmpl = this.el.querySelector<HTMLTemplateElement>(`[data-carousel-icon="${icon}"]`);
            if (svgIconTmpl) btn.appendChild(svgIconTmpl.content.cloneNode(true));
            return btn;
        };

        this.prevBtn = makekBtn('prev', this.config.labelPrev);
        this.nextBtn = makekBtn('next', this.config.labelNext);
        this.track.before(this.prevBtn, this.nextBtn);
    }

    private initNav(): void {
        if (this.config.dots) {
            this.generateDots();
        }
        this.updateBoundaryButtons();
    }

    private generateDots(): void {
        const nav = document.createElement('div');
        nav.className = 'carousel-dots';
        nav.setAttribute('role', 'group');
        nav.setAttribute('aria-label', 'Slide-Navigation');

        this.dots = this.items.map((_, i) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'carousel-dot';
            btn.setAttribute('aria-label', `Zu Slide ${i + 1} von ${this.items.length} wechseln`);
            if (i === this.activeIndex) {
                btn.setAttribute('aria-current', 'true');
                btn.setAttribute('aria-disabled', 'true');
            }
            btn.addEventListener('click', () => this.slideTo(i));
            nav.appendChild(btn);
            return btn;
        });

        this.el.insertBefore(nav, this.track);
    }

    private updateDots(): void {
        this.dots.forEach((dot, i) => {
            if (i === this.activeIndex) {
                dot.setAttribute('aria-current', 'true');
                dot.setAttribute('aria-disabled', 'true');
            } else {
                dot.removeAttribute('aria-current');
                dot.removeAttribute('aria-disabled');
            }
        });
    }

    /** Versteckte aria-live-Region für Slide-Ankündigungen. */
    private initAnnouncer(): void {
        const el = document.createElement('div');
        el.setAttribute('aria-live', 'off');
        el.setAttribute('aria-atomic', 'true');
        el.className = 'visually-hidden';
        this.el.appendChild(el);
        this.announcer = el;
    }

    /**
     * Kündigt den aktuellen Slide an.
     * Nur aufrufen wenn isUserInteraction = true; die Live-Region
     * muss bereits auf "polite" stehen, damit die Änderung vorgelesen wird.
     * Reset + rAF stellt sicher, dass der SR die Änderung zuverlässig erkennt.
     */
    private announce(): void {
        if (!this.announcer) return;
        const label = this.items[this.activeIndex].dataset.label ?? '';
        const position = `${this.activeIndex + 1} von ${this.items.length}`;
        const text = label ? `${label} \u2013 ${position}` : position;
        this.announcer.textContent = '';
        requestAnimationFrame(() => {
            this.announcer!.textContent = text;
        });
    }

    /** Prev/Next-Buttons am ersten bzw. letzten Slide als deaktiviert markieren.
     *  Wir nutzen aria-disabled statt hidden, damit der Fokus nicht verloren geht.
     *  Im Fade-Modus entfällt das, da dort geloopt wird. */
    private updateBoundaryButtons(): void {
        if (this.config.transition === 'fade') return;
        if (this.activeIndex === 0) {
            this.prevBtn?.setAttribute('aria-disabled', 'true');
        } else {
            this.prevBtn?.removeAttribute('aria-disabled');
        }
        if (this.activeIndex === this.items.length - 1) {
            this.nextBtn?.setAttribute('aria-disabled', 'true');
        } else {
            this.nextBtn?.removeAttribute('aria-disabled');
        }
    }

    // ─── Drag ────────────────────────────────────────────────────────────────

    private bindDragEvents(): void {
        let startX = 0;
        let isDragging = false;
        const THRESHOLD = 50;
        const isFade = this.config.transition === 'fade';

        const snapBack = (): void => {
            this.track.style.transition = 'transform 0.2s ease';
            void this.track.offsetHeight;
            this.track.style.transform = `translateX(-${(this.activeIndex / this.items.length) * 100}%)`;
            this.track.addEventListener('transitionend', () => {
                this.track.style.transition = '';
            }, { once: true });
        };

        const endDrag = (clientX: number): void => {
            if (!isDragging) return;
            isDragging = false;
            this.el.removeAttribute('data-dragging');

            const delta = clientX - startX;

            if (isFade) {
                if (Math.abs(delta) >= THRESHOLD) {
                    if (delta < 0) this.next();
                    else this.prev();
                } else {
                    if (this.isPlaying) this.startTimer();
                }
                return;
            }

            if (Math.abs(delta) >= THRESHOLD) {
                const goingNext = delta < 0;
                const canGo = goingNext
                    ? this.activeIndex < this.items.length - 1
                    : this.activeIndex > 0;

                if (canGo) {
                    if (goingNext) this.next();
                    else this.prev();
                } else {
                    snapBack();
                    if (this.isPlaying) this.startTimer();
                }
            } else {
                snapBack();
                if (this.isPlaying) this.startTimer();
            }
        };

        this.el.addEventListener('pointerdown', (e: PointerEvent) => {
            if ((e.target as HTMLElement).closest('button, a, [role="button"]')) return;
            if (e.button !== 0 && e.pointerType === 'mouse') return;
            if (this.isAnimating) return;
            startX = e.clientX;
            this.isDragged = false;
            isDragging = true;
            this.el.setPointerCapture(e.pointerId);
            this.el.setAttribute('data-dragging', '');
            this.stopTimer();
        });

        this.el.addEventListener('pointermove', (e: PointerEvent) => {
            if (!isDragging) return;
            if (Math.abs(e.clientX - startX) > 10) this.isDragged = true;
            if (isFade) return;
            let delta = e.clientX - startX;

            // Widerstand an den Rändern: kein leerer Bereich sichtbar
            if (this.activeIndex === 0 && delta > 0) {
                delta *= 0.2;
            } else if (this.activeIndex === this.items.length - 1 && delta < 0) {
                delta *= 0.2;
            }

            this.track.style.transition = 'none';
            this.track.style.transform = `translateX(calc(-${(this.activeIndex / this.items.length) * 100}% + ${delta}px))`;
        });

        this.el.addEventListener('pointerup', (e: PointerEvent) => {
            endDrag(e.clientX);
        });

        this.el.addEventListener('pointercancel', () => {
            endDrag(startX); // delta = 0 → Snap-Back
        });

        this.el.addEventListener('dragstart', (e) => e.preventDefault());
    }

    // ─── Slide links ─────────────────────────────────────────────────────────

    /**
     * Ghost-Click: Slides mit data-slide-link als Ganzes klickbar machen.
     * Interaktive Elemente (a, button) innerhalb des Slides lösen den Ghost-Click
     * nicht aus – sie navigieren über ihren eigenen href.
     * Nach einem Drag-Vorgang wird der Click unterdrückt (isDragged-Flag).
     */
    private bindSlideLinks(): void {
        this.items.forEach((slide) => {
            if (slide.dataset.slideLink) {
                slide.style.cursor = 'pointer';
            }
        });

        this.el.addEventListener('click', (e) => {
            if (this.isDragged) return;
if ((e.target as Element).closest('a, button, [role="button"]')) return;
            const slide = this.items[this.activeIndex];
            const url = slide?.dataset.slideLink;
            if (!url) return;
            const target = slide.dataset.slideLinkTarget;
            if (target === '_blank') {
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                window.location.href = url;
            }
        });
    }

    // ─── Navigation – aufgerufen nur durch User-Interaktion ──────────────────
    // (Buttons, Keyboard, Drag, Dots → setzen immer isUserInteraction = true)

    slideTo(index: number): void {
        this.isUserInteraction = true;
        this.slide(index);
    }

    /** Nächster Slide – im Fade-Modus mit Loop, sonst kein Effekt am letzten Slide. */
    next(): void {
        if (this.config.transition === 'fade') {
            this.isUserInteraction = true;
            this.slide((this.activeIndex + 1) % this.items.length);
        } else if (this.activeIndex < this.items.length - 1) {
            this.isUserInteraction = true;
            this.slide(this.activeIndex + 1);
        }
    }

    /** Vorheriger Slide – im Fade-Modus mit Loop, sonst kein Effekt am ersten Slide. */
    prev(): void {
        if (this.config.transition === 'fade') {
            this.isUserInteraction = true;
            this.slide((this.activeIndex - 1 + this.items.length) % this.items.length);
        } else if (this.activeIndex > 0) {
            this.isUserInteraction = true;
            this.slide(this.activeIndex - 1);
        }
    }

    // ─── Slide Engine (intern) ────────────────────────────────────────────────
    // Aufgerufen durch Navigation (next/prev/slideTo) und Autoplay (tick).
    // slide() ist der einzige Einstiegspunkt – slideTrack/slideFade nie direkt.

    private slide(nextIndex: number): void {
        if (this.isAnimating || nextIndex === this.activeIndex) return;
        this.isAnimating = true;

        if (this.config.transition === 'fade') {
            this.slideFade(nextIndex);
        } else {
            this.slideTrack(nextIndex);
        }
    }

    private slideTrack(nextIndex: number): void {
        const prevIndex = this.activeIndex;

        // Inline-Override (z.B. 'none' vom Drag) zurücksetzen,
        // damit die CSS-Transition aus dem Stylesheet greift
        this.track.style.transition = '';
        void this.track.offsetHeight; // Reflow: Browser sieht Ausgangsposition

        this.track.style.transform = `translateX(-${(nextIndex / this.items.length) * 100}%)`;

        let done = false;
        const cleanup = (): void => {
            if (done) return;
            done = true;
            this.track.style.transition = '';
            this.items[prevIndex].removeAttribute('data-active');
            this.items[nextIndex].setAttribute('data-active', '');
            this.afterSlide(nextIndex);
        };
        this.track.addEventListener('transitionend', cleanup, { once: true });
        setTimeout(cleanup, 700);
    }

    private slideFade(nextIndex: number): void {
        const prevIndex = this.activeIndex;

        // data-active vor cleanup setzen – die CSS-Transition auf dem neuen
        // Slide wird dadurch ausgelöst, ihr transitionend ist unser Signal.
        this.items[prevIndex].removeAttribute('data-active');
        this.items[nextIndex].setAttribute('data-active', '');

        let done = false;
        const cleanup = (): void => {
            if (done) return;
            done = true;
            this.afterSlide(nextIndex);
        };

        this.items[nextIndex].addEventListener('transitionend', cleanup, { once: true });
        setTimeout(cleanup, 700);
    }

    /** Gemeinsame Abschlusslogik nach jeder Slide-Animation (Track und Fade). */
    private afterSlide(nextIndex: number): void {
        this.activeIndex = nextIndex;
        this.isAnimating = false;
        this.updateDots();
        this.updateAriaHidden();
        this.updateBoundaryButtons();
        // Ankündigung: nur bei manueller Interaktion, nicht beim Autoplay
        if (this.announcer) {
            this.announcer.setAttribute('aria-live', this.isUserInteraction ? 'polite' : 'off');
            if (this.isUserInteraction) this.announce();
        }
        this.isUserInteraction = false;
        // Timer neu starten – aber nicht wenn der Fokus noch im Karussell liegt
        // (Tastaturnavigation: Fokus bleibt auf Prev/Next/Dot-Button).
        if (this.isPlaying && !this.el.contains(document.activeElement)) this.startTimer();
    }

    private updateAriaHidden(): void {
        this.items.forEach((item, i) => {
            if (i === this.activeIndex) {
                item.removeAttribute('aria-hidden');
                item.removeAttribute('inert');
            } else {
                item.setAttribute('aria-hidden', 'true');
                item.setAttribute('inert', '');
            }
        });
    }

    // ─── Autoplay – Public API ────────────────────────────────────────────────
    // Aufgerufen durch: Pause-Button (onPlay/onPause), initial play() im Konstruktor.

    play(): void {
        this.isPlaying = true;
        this.startTimer();
        this.announcer?.setAttribute('aria-live', 'off');
    }

    pause(): void {
        this.isPlaying = false;
        this.stopTimer();
        this.announcer?.setAttribute('aria-live', 'polite');
    }

    // ─── Autoplay Timer (intern) ──────────────────────────────────────────────
    // tick() wird nur durch setInterval aufgerufen – nie durch User-Interaktion.

    /**
     * Einen Tick weiter – im Fade-Modus mit Loop, sonst Bounce:
     * Am letzten Slide kehrt die Richtung um, statt zum ersten zu springen.
     */
    private tick(): void {
        this.isUserInteraction = false;
        if (this.config.transition === 'fade') {
            this.slide((this.activeIndex + 1) % this.items.length);
            return;
        }
        const nextIndex = this.activeIndex + this.direction;
        if (nextIndex < 0 || nextIndex >= this.items.length) {
            this.direction *= -1;
            this.slide(this.activeIndex + this.direction);
        } else {
            this.slide(nextIndex);
        }
    }

    private startTimer(): void {
        this.stopTimer();
        if (this.config.interval > 0) {
            this.timer = setInterval(() => this.tick(), this.config.interval);
        }
    }

    private stopTimer(): void {
        if (this.timer !== null) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }

    private addPauseButton(): void {
        if (this.el.querySelector('.carousel-pause-button')) return;

        this.pauseButton = createPauseButton({
            className: 'carousel-pause-button',
            onPlay: () => this.play(),
            onPause: () => this.pause(),
        });
        this.el.prepend(this.pauseButton.element);
    }
}

export function initCarousels(): void {
    const carousels = document.querySelectorAll<HTMLElement>('.carousel');
    carousels.forEach(el => new Carousel(el));
}
